import { MAX_CONTAINER_WIDTH } from '@/components/layout/Container';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import {
  applyBreakpointPadding,
  marginX,
  marginY,
  mediaBreakpointDown,
  mediaBreakpointUp,
  paddingX,
  paddingY,
} from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import styled from 'styled-components';

const PageWrapper = styled.div`
  ${marginX('auto')}
  margin-top: ${remCalc(PRIMARY_NAV_HEIGHT)};
`;

const MainContent = styled.article`
  position: relative;
  z-index: 2;
  border-radius: ${remCalc(10, 10, 0, 0)};
  min-height: ${remCalc(50)};
  background-color: ${COLORS.WHITE};
  border-radius: 20px;

  display: grid;
  grid-template-columns: auto;
  grid-template-rows: auto auto auto;
  grid-row-gap: ${remCalc(40)};

  ${applyBreakpointPadding};

  margin: ${remCalc(PRIMARY_NAV_HEIGHT)} auto ${remCalc(80)};
  max-width: ${remCalc(MAX_CONTAINER_WIDTH)};
  box-sizing: content-box;
  padding-top:  ${remCalc(40)};

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: ${remCalc(250)} auto;
    grid-template-rows: auto;
    grid-column-gap: ${remCalc(60)};
    grid-row-gap: 0;
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-template-columns: ${remCalc(300)} auto;
  }
`;

const ChapterListColumn = styled.aside`
  ${mediaBreakpointUp('desktop')} {
    grid-column: 1;
  }
`;

const ChapterListWrapper = styled.nav`
  position: sticky;
  top: ${remCalc(PRIMARY_NAV_HEIGHT + 50)};

  background-color: ${COLORS.GRAY.HIGHLIGHT};
  border-radius: ${remCalc(10)};
  min-height: ${remCalc(250)};

  padding: ${remCalc(31, 8, 8)};

  ${mediaBreakpointDown('tablet')} {
    margin-left: calc(var(--breakpoint-padding-x) / -2);
    margin-right: calc(var(--breakpoint-padding-x) / -2);
  }
`;

const ChapterListHeading = styled.div`
  ${customTextSize(24, 30)}
  font-weight: ${FONT_WEIGHTS.MEDIUM};
  ${paddingX(24)}
  margin-bottom: ${remCalc(8)};
`;

const ChapterList = styled.ol``;

/* prettier-ignore */
const ChapterListItem = styled.li<{ level: number }>`
  position: relative;

  a {
    display: block;
    ${(props) => paddingX(24 + ((props.level - 2) * 16), 24)}
    ${paddingY(8)}
    font-size: ${(props) => 100 - ((props.level - 2) * 5)}%;

    border-radius: ${remCalc(10)};
    background-color: transparent;

    transition: background-color 0.15s ease-in-out;

    color: ${COLORS.BLACK};

    &:hover {
      background-color: ${hexToRGBA(COLORS.GRAY.BASE, 0.25)};
    }

    span {
      display: block;
    }
  }

  &::before {
    content: '';
    width: ${remCalc(6)};
    height: ${remCalc(6)};
    position: absolute;
    top: ${remCalc(18)};
    left: ${remCalc(6)};
    border-radius: 100%;
    transition: background-color 0.15s ease-in-out;
  }
`;

const Content = styled.div<{ fullWidth: boolean }>`
  ${mediaBreakpointUp('desktop')} {
    grid-column: ${(props) => (props.fullWidth ? '1 / span 2' : '2 / span 10')};
    max-width: 900px;
    margin: 0 auto;
  }
`;

const ChapterWrapper = styled.div`
  margin-top: ${remCalc(40)};

  ${mediaBreakpointUp('desktop')} {
    margin-top: ${remCalc(100)};
  }
`;

const HTMLContainer = styled.article`
  ${customTextSize(16, 24)};

  > * + * {
    margin-top: 1rem;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }

  h1 {
    ${customTextSize(48, 54)};
    margin-bottom: ${remCalc(26)};

    ${mediaBreakpointUp('desktop')} {
      ${customTextSize(72, 78)};
      margin-bottom: ${remCalc(40)};
    }
  }

  h2 {
    ${customTextSize(32, 38)};
    ${marginY(26)}

    ${mediaBreakpointUp('desktop')} {
      ${customTextSize(49, 58)};
      ${marginY(40)}
    }
  }

  h3 {
    ${customTextSize(24, 30)};

    ${mediaBreakpointUp('desktop')} {
      ${customTextSize(32, 38)};
      ${marginY(32)}
    }
  }

  h4 {
    ${customTextSize(16, 21)};
    font-weight: ${FONT_WEIGHTS.BOLD};
    ${marginY(22)}

    ${mediaBreakpointUp('desktop')} {
      ${customTextSize(24, 24)};
      ${marginY(32)}
    }
  }

  h5 {
    font-size: 110%;
  }

  h6 {
    font-size: 100%;
  }

  a {
    border-bottom: 1px solid;
  }

  table {
    &,
    tr,
    td {
      border-width: 1px;
      border-color: ${COLORS.GRAY.HIGHLIGHT};
    }

    tr,
    td {
      padding: ${remCalc(5, 10)};
    }

    td p {
      margin: 0;
    }

    tr:first-of-type {
      font-weight: ${FONT_WEIGHTS.SEMIBOLD};

      td p {
        text-align: center;
      }
    }
  }

  ul,
  ol {
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    padding-inline-start: 40px;

    li + li {
      margin-top: 1rem;
    }
  }

  ul {
    list-style-type: disc;

    ul {
      list-style-type: circle;
    }
  }

  ol {
    list-style-type: decimal;

    ol {
      list-style-type: lower-alpha;

      ol {
        list-style-type: lower-roman;
      }
    }
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(18, 26)};
  }
`;

const htmlPageComponents = {
  PageWrapper,
  MainContent,
  ChapterListColumn,
  ChapterListWrapper,
  ChapterListHeading,
  ChapterList,
  ChapterListItem,
  Content,
  ChapterWrapper,
  HTMLContainer,
};

export default htmlPageComponents;
